import { useEffect, useState, useRef } from "react"
import { ChevronDown, ChevronUp, Plus, Minus } from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"
import { locationsApi } from "api"

export const GeneralInfoSection = ({
  data,
  editableData,
  editMode,
  onChange,
  currency,
  platform,
  loading
}) => {
  const [expandedLocationAdsets, setExpandedLocationAdsets] = useState(true)
  const [expandedBudgetAdsets, setExpandedBudgetAdsets] = useState(true)
  const [countriesData, setCountriesData] = useState({ google_countries: {}, meta_countries: {} })
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false)
  const [countrySearch, setCountrySearch] = useState("")
  const dropdownRef = useRef()

  useEffect(() => {
    const getCountries = async () => {
      const result = await locationsApi.fetchCountries()
      setCountriesData(result)
    }
    getCountries()
  }, [])

  useEffect(() => {
    const handleClickOutside = e => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setCountryDropdownOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const providerCountries =
    platform === "google"
      ? countriesData.google_countries
      : countriesData.meta_countries

  const optionsList = Object.entries(providerCountries).map(([name, code]) => ({
    name,
    value: code,
  }))

  const renderName = () =>
    editMode ? (
      <input
        type="text"
        value={editableData.name}
        onChange={e => onChange("name", e.target.value)}
        className={
          `w-full border border-white/20 rounded-xl px-4 py-2.5 text-sm text-white bg-white/10 focus:outline-none focus:ring-2 focus:ring-fuchsia-400/20 focus:border-fuchsia-400 transition-all` +
          (loading ? " disabled" : "")
        }
        placeholder="Nombre de la campaña"
      />
    ) : (
      editableData.name || "Sin definir"
    )  

  const renderBudget = () => {
    const daily = editableData.daily_budget

    if (!currency) {
      return (
        <div className="h-6 w-24 bg-white/20 animate-pulse rounded"></div>
      )
    }

    if (!editMode) {
      const total =
        typeof daily === "object"
          ? Object.values(daily).reduce((sum, v) => sum + Number(v || 0), 0)
          : Number(daily || 0)
      return total > 0 ? (
        <div className="text-sm font-medium">
          {currency} {total.toLocaleString()} por día
        </div>
      ) : (
        "Sin definir"
      )
    }

    if (typeof daily === "object") {
      const adsets = Object.entries(daily).map(([adsetId, value]) => {
        const adsetName =
          data.ad_groups?.find(ag => ag.id === adsetId)?.name ||
          `Adset ${adsetId}`
        return { id: adsetId, name: adsetName, value }
      })

      const totalBudget = Object.values(daily).reduce(
        (sum, v) => sum + Number(v || 0),
        0
      )

      return (
        <div className={"space-y-4" + (loading ? " disabled" : "")}>
          <div className="flex items-center justify-between bg-white/5 rounded-xl px-4 cursor-pointer py-3 border border-white/20" onClick={() => setExpandedBudgetAdsets(e => !e)}>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-white">Presupuesto total</span>
              <span className="text-xs px-2 py-0.5 bg-white/10 rounded-full text-white/70">
                {currency} {totalBudget.toLocaleString()}
              </span>
            </div>
            <button
              className="text-white/70 hover:text-white transition-colors p-1 rounded-full hover:bg-white/20"
            >
              {expandedBudgetAdsets ? (
                <ChevronUp size={16} />
              ) : (
                <ChevronDown size={16} />
              )}
            </button>
          </div>

          <AnimatePresence>
            {expandedBudgetAdsets && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="space-y-2 overflow-hidden"
              >
                {adsets.map(adset => (
                  <motion.div
                    key={adset.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                    className={"bg-white/10 border border-white/20 rounded-xl p-4 flex items-center justify-between hover:bg-white/15 transition-all" + (loading ? " disabled" : "")}
                  >
                    <span className="flex-1 truncate text-sm font-medium text-white">
                      {adset.name}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-white/70">
                        {currency}
                      </span>
                      <div className="flex items-center border border-white/20 rounded-lg overflow-hidden bg-white/10">
                        <button
                          onClick={() => {
                            const cv = Number(adset.value || 0)
                            if (cv > 0) {
                              onChange("daily_budget", {
                                ...daily,
                                [adset.id]: cv - 1,
                              })
                            }
                          }}
                          className="px-2 py-1.5 text-white/70 hover:bg-white/20 transition-colors"
                          disabled={Number(adset.value || 0) <= 0}
                        >
                          <Minus size={14} />
                        </button>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={adset.value === "" ? "" : adset.value}
                          onChange={e => {
                            const num =
                              e.target.value === ""
                                ? ""
                                : Math.max(
                                    0,
                                    Math.floor(Number(e.target.value))
                                  )
                            onChange("daily_budget", {
                              ...daily,
                              [adset.id]: num,
                            })
                          }}
                          className="w-16 text-center border-x border-white/20 py-1.5 text-sm text-white bg-white/5 focus:outline-none"
                        />
                        <button
                          onClick={() =>
                            onChange("daily_budget", {
                              ...daily,
                              [adset.id]:
                                Number(adset.value || 0) + 1,
                            })
                          }
                          className="px-2 py-1.5 text-white/70 hover:bg-white/20 transition-colors"
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
                <div className="text-xs text-white/50 text-right italic">
                  por día
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )
    }

    return (
      <div className={"flex items-center gap-2" + (loading ? " disabled" : "")}>
        <span className="font-medium text-white/70">{currency}</span>
        <div className="flex items-center border border-white/20 rounded-lg overflow-hidden bg-white/10">
          <button
            onClick={() => {
              const currentValue = Number(daily || 0)
              if (currentValue > 0) {
                onChange("daily_budget", currentValue - 1)
              }
            }}
            className="px-2 py-1.5 text-white/70 hover:bg-white/20 transition-colors"
            disabled={Number(daily || 0) <= 0}
          >
            <Minus size={14} />
          </button>
          <input
            type="number"
            min="0"
            step="1"
            value={daily === "" || daily == null ? "" : daily}
            onChange={e => {
              const num =
                e.target.value === ""
                  ? ""
                  : Math.max(0, Math.floor(Number(e.target.value)))
              onChange("daily_budget", num)
            }}
            className="w-24 text-center border-x border-white/20 py-1.5 text-sm text-white bg-white/5 focus:outline-none"
          />
          <button
            onClick={() => {
              const currentValue = Number(daily || 0)
              onChange("daily_budget", currentValue + 1)
            }}
            className="px-2 py-1.5 text-white/70 hover:bg-white/20 transition-colors"
          >
            <Plus size={14} />
          </button>
        </div>
        <span className="text-xs text-white/50">por día</span>
      </div>
    )
  }

  const renderLocation = () => {
    const campaignLocs = data.targeting?.locations || [];
    const adGroups     = data.ad_groups   || [];
  
    if (!editMode) {
      if (campaignLocs.length > 0) {
        return campaignLocs.map(loc => loc.name).join(', ');
      }
      return 'Sin definir';
    }
  
    const isCampaignLevel = platform === 'google' || adGroups.length === 1;
  
    if (isCampaignLevel) {
      const current = Array.isArray(editableData.locations)
        ? editableData.locations
        : campaignLocs.map(loc =>
            platform === 'google' ? loc.id : loc.country_code
          );
  
      const label = current.length > 0
        ? optionsList
            .filter(opt => current.includes(opt.value))
            .map(opt => opt.name)
            .join(', ')
        : 'Seleccionar países';
  
      return (
        <div className={"relative" + (loading ? " disabled" : "")} ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setCountryDropdownOpen(o => !o)}
            className="w-full text-left border border-white/20 rounded-xl px-4 py-2 text-sm text-white bg-white/10 flex items-center justify-between"
          >
            <span className="truncate">{label}</span>
            <ChevronDown
              size={16}
              className={`${countryDropdownOpen ? 'rotate-180' : ''} transition-transform text-white/70`}
            />
          </button>
          {countryDropdownOpen && (
            <div className="absolute z-50 mt-1 w-full bg-slate-800 border border-white/20 rounded-xl shadow-lg p-4 max-h-60 overflow-auto">
              <input
                type="text"
                placeholder="Buscar..."
                value={countrySearch}
                onChange={e => setCountrySearch(e.target.value)}
                className="w-full mb-2 border border-white/20 rounded-lg px-3 py-1 text-sm text-white bg-white/10 focus:outline-none focus:border-fuchsia-400"
              />
              {optionsList
                .filter(opt => opt.name.toLowerCase().includes(countrySearch.toLowerCase()))
                .map(opt => (
                  <label
                    key={opt.value}
                    className="flex items-center gap-2 text-sm py-1 hover:bg-white/10 rounded cursor-pointer px-2 text-white"
                  >
                    <input
                      type="checkbox"
                      checked={current.includes(opt.value)}
                      onChange={() => {
                        const updated = current.includes(opt.value)
                          ? current.filter(v => v !== opt.value)
                          : [...current, opt.value];
                        onChange('locations', updated);
                      }}
                      className="form-checkbox h-4 w-4 text-primary"
                    />
                    <span className="truncate">{opt.name}</span>
                  </label>
                ))}
            </div>
          )}
        </div>
      );
    }
  
    return (
        <div className={"space-y-4" + (loading ? " disabled" : "")}>
        <div className="flex items-center justify-between bg-white/5 cursor-pointer rounded-xl px-4 py-3 border border-white/20" onClick={() => setExpandedLocationAdsets(e => !e)}>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-white">Localización</span>
            <span className="text-xs px-2 py-0.5 bg-white/10 rounded-full text-white/70">
              {adGroups.reduce((sum, ag) => {
                const vals = editableData.locations?.[ag.id]
                  ?? ag.targeting?.geo_locations?.countries
                  ?? [];
                return sum + vals.length;
              }, 0)} países
            </span>
          </div>
          <button
            className="text-white/70 hover:text-white p-1 rounded-full hover:bg-white/20"
          >
            {expandedLocationAdsets ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}
          </button>
        </div>
        <AnimatePresence>
          {expandedLocationAdsets && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="space-y-2 overflow-visible"
            >
              {adGroups.map(ag => {
                const current = editableData.locations?.[ag.id]
                  ?? ag.targeting?.geo_locations?.countries
                  ?? [];
                return (
                  <motion.div
                    key={ag.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                    className="bg-white/10 border border-white/20 rounded-xl p-4"
                  >
                    <div className="text-sm font-medium mb-2 text-white">{ag.name}</div>
                    <div
                      className="relative overflow-visible"
                      tabIndex={0}
                      onBlur={e => {
                        if (!e.currentTarget.contains(e.relatedTarget)) {
                          setCountryDropdownOpen(null);
                        }
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => setCountryDropdownOpen(ag.id)}
                        className="w-full text-left border border-white/20 rounded-xl px-4 py-2 text-sm text-white bg-white/10 flex items-center justify-between"
                      >
                        <span className="truncate">
                          {current.length > 0
                            ? optionsList
                                .filter(opt => current.includes(opt.value))
                                .map(opt => opt.name)
                                .join(', ')
                            : 'Seleccionar países'}
                        </span>
                        <ChevronDown
                          size={16}
                          className={`transition-transform text-white/70 ${countryDropdownOpen === ag.id ? 'rotate-180' : ''}`}
                        />
                      </button>
                      {countryDropdownOpen === ag.id && (
                        <div className="absolute z-50 mt-1 w-full bg-slate-800 border border-white/20 rounded-xl shadow-lg p-4 max-h-60 overflow-auto">
                          <input
                            type="text"
                            placeholder="Buscar..."
                            value={countrySearch}
                            onChange={e => setCountrySearch(e.target.value)}
                            className="w-full mb-2 border border-white/20 rounded-lg px-3 py-1 text-sm text-white bg-white/10 focus:outline-none focus:border-fuchsia-400"
                          />
                          {optionsList
                            .filter(opt => opt.name.toLowerCase().includes(countrySearch.toLowerCase()))
                            .map(opt => (
                              <label
                                key={opt.value}
                                className="flex items-center gap-2 text-sm py-1 hover:bg-white/10 rounded cursor-pointer px-2 text-white"
                              >
                                <input
                                  type="checkbox"
                                  checked={current.includes(opt.value)}
                                  onChange={() => {
                                    const updated = current.includes(opt.value)
                                      ? current.filter(v => v !== opt.value)
                                      : [...current, opt.value];
                                    onChange('locations', {
                                      ...editableData.locations,
                                      [ag.id]: updated
                                    });
                                  }}
                                  className="form-checkbox h-4 w-4 text-primary"
                                />
                                <span className="truncate">{opt.name}</span>
                              </label>
                            ))}
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };
   
  
  return (
    <div className="font-sans">
      <h3 className="text-sm font-medium text-white/70 mb-3">
        Información general
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="bg-white/10 rounded-xl p-4 sm:col-span-4 border border-white/20">
          <span className="text-xs text-white/70 block mb-1.5">
            Nombre de la campaña
          </span>
          <span className="text-sm text-white block break-words">
            {renderName()}
          </span>
        </div>
        <div className="bg-white/10 rounded-xl p-4 sm:col-span-2 border border-white/20">
          <span className="text-xs text-white/70 block mb-1.5">
            Presupuesto total
          </span>
          <div className="text-sm text-white">{renderBudget()}</div>
        </div>
        <div className="bg-white/10 rounded-xl p-4 sm:col-span-2 border border-white/20">
          <span className="text-xs text-white/70 block mb-1.5">
            Localización
          </span>
          <div className="text-sm text-white">{renderLocation()}</div>
        </div>
      </div>
    </div>
  )
}

export default GeneralInfoSection
